import React, { useEffect, useMemo, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from 'components/navbar';
import Sidebar from 'components/sidebar';
import routes from 'routes';
import { Link } from 'react-router-dom';
import { ICallAdmin, NotifyCategory } from 'views/types/support';
import { toast } from 'App';
import { useSSEBroadcast } from 'hooks/useSSEBroadcast';
import { useInactivityTimer } from 'hooks/useInactivityTimer';
import { API_URL } from 'views/services/interceptor';
import { adminService } from 'views/services/adminService';
import { useAdminsStore } from 'views/store/useAdminsStore';
import { usePermissions } from 'hooks/usePermissions';
import { ROUTE_TO_RESOURCE_MAP } from 'constants/permissions';

export default function Admin(props: { [x: string]: unknown }) {
  const { ...rest } = props;
  const location = useLocation();
  const [open, setOpen] = useState(true);
  const [currentRoute, setCurrentRoute] = useState('Dashboard');
  const { currentAdmin } = useAdminsStore();
  const { canRead, loading: permissionsLoading } = usePermissions();

  useInactivityTimer();

  const filteredRoutes = useMemo(() => {
    if (!currentAdmin || permissionsLoading) {
      return [];
    }

    return routes.filter(route => {
      const resource = ROUTE_TO_RESOURCE_MAP[route.path];
      const hasPermissionAccess = !resource || canRead(resource);

      return hasPermissionAccess;
    });
  }, [currentAdmin, canRead, permissionsLoading]);

  useSSEBroadcast({
    url:
      (API_URL.endsWith('/') ? API_URL : `${API_URL}/`) +
      'api/admins-notifications',
    onOpen: () => {
      console.log('SSE connection opened');
    },
    onMessage: data => {
      console.log('Notification received:', data);
    },

    listeners: {
      [NotifyCategory.CALL_ADMIN]: (data: ICallAdmin) => {
        toast({
          title: 'New Notification',
          description: (
            <>
              <Link to="/admin/support-chats">@{data.user?.username}</Link>{' '}
              needs your attention
            </>
          ),
          status: 'info',
          duration: 30000,
          isClosable: true,
        });
      },
      [NotifyCategory.SUPPORT_TICKET]: (data: ICallAdmin) => {
        toast({
          title: 'New Support Ticket',
          description: (
            <>
              You have a new ticket from{' '}
              <Link to="/admin/support-chats">@{data.user?.username}</Link>
            </>
          ),
          status: 'info',
          duration: 30000,
          isClosable: true,
        });
      },
    },
  });

  useEffect(() => {
    window.addEventListener('resize', () =>
      window.innerWidth < 1200 ? setOpen(false) : setOpen(true)
    );

    adminService.getCurrentAdmin();
  }, []);

  useEffect(() => {
    getActiveRoute(filteredRoutes);
  }, [filteredRoutes, location.pathname]);

  const getActiveRoute = (propRoutes: RoutesType[]): string | boolean => {
    const activeRoute = 'Dashboard';
    for (let i = 0; i < propRoutes.length; i++) {
      if (
        window.location.href.indexOf(
          propRoutes[i].layout + '/' + propRoutes[i].path
        ) !== -1
      ) {
        setCurrentRoute(propRoutes[i].name);
      }
    }
    return activeRoute;
  };

  const getActiveNavbar = (propRoutes: RoutesType[]): string | boolean => {
    const activeNavbar = false;
    for (let i = 0; i < propRoutes.length; i++) {
      if (
        window.location.href.indexOf(
          propRoutes[i].layout + propRoutes[i].path
        ) !== -1
      ) {
        return propRoutes[i].secondary;
      }
    }
    return activeNavbar;
  };

  const getRoutes = (propRoutes: RoutesType[]) => {
    return propRoutes.map((prop, key) => {
      if (prop.layout === '/admin') {
        return (
          <Route path={`/${prop.path}`} element={prop.component} key={key} />
        );
      } else {
        return null;
      }
    });
  };

  document.documentElement.dir = 'ltr';

  return (
    <div className="flex min-h-[100svh] overflow-y-hidden w-full">
      <Sidebar open={open} onClose={() => setOpen(false)} />
      {/* Navbar & Main Content */}
      <div className="w-full bg-lightPrimary dark:!bg-navy-900">
        {/* Main Content */}
        <main
          className={`mx-[12px] h-full flex-none transition-all md:pr-2 xl:ml-[313px]`}
        >
          {/* Routes */}
          {currentAdmin && filteredRoutes?.length && (
            <div className="h-full">
              <Navbar
                isSideNavOpen={open}
                onSideNavOpenChange={setOpen}
                brandText={currentRoute}
                secondary={getActiveNavbar(filteredRoutes)}
                {...rest}
              />
              <div className="pt-5 mx-auto mb-auto min-h-full p-2 md:pr-2">
                <Routes>
                  {getRoutes(filteredRoutes)}

                  <Route
                    path="/"
                    element={
                      <Navigate
                        to={'/admin/' + filteredRoutes[0].path}
                        replace
                      />
                    }
                  />
                </Routes>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
